import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import { usePending } from "../hooks/usePending";
import Topbar from "./Topbar";
import ContactDetailPage from "./ContactDetailPage";
import useEscapeLayer from "./useEscape";
import DateField from "./datepicker/DateField";

/*
 * THE APPROVALS QUEUE — the Monday surface.
 *
 * A row is a proposal, or several proposals that are ONE verdict (the ghost
 * sweep writes the company's exit and its POC's stamp separately; the server
 * groups them under one group_key). Every row can be answered three ways:
 *
 *   Approve   write the payload the rule wrote
 *   Approve as…  write a DIFFERENT ending — the caret, and the reason this page
 *                was redesigned. The sweep proposes lost/'ghosted' on a company
 *                that is really a park, and before this the verdict cost two
 *                gestures in two places and recorded a no.
 *   Dismiss   the rule fired on nothing
 *
 * "Leave them in the pipeline" is NOT a correction, and the menu says so in
 * words rather than leaving it to be discovered: it is a dismissal, and the
 * dismissal is the white button.
 */

const STAGE_LABEL = {
  new: "New", call_scheduled: "Call scheduled", in_progress: "In progress", evaluating: "Evaluating",
  proposal_sent: "Proposal sent", agreement: "Agreement",
  salesqualifiedlead: "SQL", marketingqualifiedlead: "MQL", opportunity: "Opportunity",
  customer: "Customer", lead: "Lead", subscriber: "Subscriber", other: "Other",
};
const STATUS_LABEL = {
  prospect: "Prospect", client: "Client", churned: "Churned", lost: "Lost",
  not_a_fit: "Not a fit", advisory: "Advisory", vendor: "Vendor", recycled: "Recycled",
};
const STATUS_VERB = {
  client: "mark Client", churned: "mark Churned", lost: "mark Lost", not_a_fit: "Disqualify",
  recycled: "park (Recycled)",
};

/*
 * The badge says what NOTICED, in four letters. The rule where the server named
 * one (migration 037) and the evidence source otherwise — a sweep counting
 * silence and a person replying are the same status change from opposite kinds
 * of certainty, and only one of them is worth overruling on a hunch.
 */
const BADGE = {
  ghost_sweep: "GHOST", cadence_exhausted: "GHOST", ignored_cleanup: "GHOST",
  re_engage_wakeup: "WAKE", follow_up_rule: "RULE",
  merge_duplicate: "DUPE", auditor_drift: "SYNC", auditor_churned: "SYNC", ad_budget_sync: "SYNC",
  meeting_booked: "CAL", mcp: "CHAT",
};
const SOURCE_BADGE = {
  gmail: "MAIL", gmail_pandadoc: "DOC", fireflies: "CALL", calendar: "CAL", mcp: "CHAT", rule: "RULE",
};
const LINK_LABEL = {
  fireflies: "Transcript", gmail_pandadoc: "PandaDoc", gmail: "Thread", calendar: "Event",
};

// The endings a correction can name, and what each one needs. Every field here
// is enforced by the API too (updateCompanyPipeline). Since 2026-09-30 the park
// is "out of the pipeline": ghosting, no budget and stop-chasing all land here,
// never in lost or not a fit, and its wake-up date is optional.
const ENDINGS = {
  park: {
    label: "Out of pipeline",
    hint: "ghosted · no budget…",
    status: "recycled",
    reasons: [
      ["ghosted", "Ghosted"], ["stopped_chasing", "Stopped chasing"], ["no_budget_now", "No budget now"],
      ["timing", "Timing"], ["browsing", "Just browsing"], ["misaligned", "Misaligned"], ["ignored", "Ignored"],
    ],
    date: { field: "recycle_until", label: "Remind me on", required: false },
  },
  lost: {
    label: "Lost, other reason",
    hint: "competitor · declined…",
    status: "lost",
    reasons: [
      ["chose_competitor", "Chose a competitor"], ["declined", "Declined after pricing"],
      ["closed_out", "We closed it out"],
    ],
    date: { field: "re_engage_after", label: "Ping them again on", required: false },
  },
  not_a_fit: {
    label: "Not a fit",
    hint: "never again",
    status: "not_a_fit",
    reasons: [["junk", "Junk"], ["bad_fit", "Bad fit"]],
    date: null,
  },
};

/*
 * NOT AN ENDING AT ALL: the person was never in the market (a friend, a
 * supplier's rep, a bot's address). It is the same purge-and-ignore verdict the
 * Leads list and the contact page offer (POST /contacts/:id/not-a-lead), not a
 * company status, so it sits outside ENDINGS and never goes through /correct.
 * Offered only where the server would take it: a row with a person behind it,
 * at a company that is not a client or an ex-client.
 */
const canNotALead = (g) => !!g.contactId && !["client", "churned"].includes(g.companyStatus);

// "Proton (proton.ai)": a merge names both rows, and two rows called Proton and
// Proton.ai are told apart by the domain as much as by the name.
const companyRef = (name, domain) => `${name} (${domain || "no domain"})`;

function describeChange(c) {
  const p = c.payload || {};
  switch (c.change_type) {
    case "company_stage": return `move to ${STAGE_LABEL[p.to] || p.to}`;
    case "contact_stage": return `contact → ${STAGE_LABEL[p.to] || p.to}`;
    case "company_status":
      if (p.to === "prospect") {
        return `return to pipeline${p.pipeline_stage ? ` (${STAGE_LABEL[p.pipeline_stage] || p.pipeline_stage})` : ""}`;
      }
      // A sweep's park (rules 13 and 2b) names why and for how long: "park
      // (ghosted, 6 months)". A bare "park (Recycled)" hid that it is not a loss.
      if (p.to === "recycled" && p.status_reason) {
        const months = p.recycle_for_days ? `, ${Math.round(p.recycle_for_days / 30)} months` : "";
        return `park (${p.status_reason}${months})`;
      }
      return p.to === "not_a_fit" || p.to === "lost"
        ? `${STATUS_VERB[p.to]} (${p.status_reason || "?"})`
        : STATUS_VERB[p.to] || `→ ${p.to}`;
    case "add_to_pipeline": return "add as lead";
    case "set_value":
      if (p.lead_status) return `lead → ${p.lead_status}`;
      return `budget ${p.currency || ""} ${p.monthly_value ?? ""}/mo`.trim();
    case "create_contact": return `new contact ${p.email || p.name || ""}`;
    case "merge_company":
      return p.loser_name ? `fold in duplicate ${companyRef(p.loser_name, p.loser_domain)}` : "merge duplicate company";
    case "set_quote": return `new pricing: ${(p.terms || []).join(" · ")}`;
    default: return c.change_type;
  }
}

// One line per change in the panel's THIS ROW PROPOSES block: the subject, where
// it is now, and where the proposal would put it. Written from → to rather than
// as a verb, because the panel is where you check the move rather than read it.
function proposalLine(c) {
  const p = c.payload || {};
  const person = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(" ");
  if (c.change_type === "company_status") {
    const from = c.company_current_status === "prospect"
      ? STAGE_LABEL[c.company_current_stage] || "Prospect"
      : STATUS_LABEL[c.company_current_status] || c.company_current_status;
    const reason = p.status_reason ? ` · ${p.status_reason}` : "";
    return { subject: c.company_name, body: `${from} → ${STATUS_LABEL[p.to] || p.to}${reason}` };
  }
  if (c.change_type === "company_stage") {
    return {
      subject: c.company_name,
      body: `${STAGE_LABEL[c.company_current_stage] || "—"} → ${STAGE_LABEL[p.to] || p.to}`,
    };
  }
  if (c.change_type === "contact_stage") {
    return {
      subject: person || c.contact_email,
      body: `${STAGE_LABEL[c.contact_current_stage] || "—"} → ${STAGE_LABEL[p.to] || p.to}`,
    };
  }
  // The quote is the subject, so the line says what it was and what it becomes.
  if (c.change_type === "set_quote") {
    const was = (p.replaces || []).join(" · ");
    return { subject: c.company_name, body: `${was || "no pricing on record"} → ${(p.terms || []).join(" · ")}` };
  }
  // Which row goes and which stays, both by name: the queue row is filed under
  // the survivor, so without this the other company appears nowhere.
  if (c.change_type === "merge_company" && p.loser_name) {
    return {
      subject: c.company_name,
      body: `${companyRef(p.loser_name, p.loser_domain)} folds into ${companyRef(p.survivor_name || c.company_name, p.survivor_domain)}`,
    };
  }
  if (c.change_type === "set_value" && p.lead_status) {
    return { subject: person || c.contact_email, body: `lead → ${p.lead_status}` };
  }
  return { subject: c.company_name || person, body: describeChange(c) };
}

const BLOCK_REASON = {
  STAGE_ON_NON_PROSPECT: "this company is closed, so a pipeline stage no longer applies — the move it proposes is out of date",
  PIPELINE_STAGE_REQUIRED: "returning a company to the pipeline needs a stage, and this proposal carries none",
};
function blockText(failure) {
  if (!failure) return null;
  return BLOCK_REASON[failure.code] || failure.error || "could not be applied";
}

const shortDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
const dayDate = (v) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null);
const isToday = (v) => !!v && new Date(v).toDateString() === new Date().toDateString();

function groupRows(changes) {
  const groups = new Map();
  for (const c of changes) {
    // group_key comes from the server (proposal_group, else evidence_ref, else
    // the row id). Which proposals are ONE verdict is a fact about them, not a
    // drawing decision — deriving it here from evidence_ref alone is what
    // rendered the ghost sweep's company exit and its POC stamp as two rows you
    // could answer contradictorily. The fallback keeps an older server (or a
    // cached response) rendering as it did before.
    const key = c.group_key || c.evidence_ref || `id:${c.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  return [...groups.entries()].map(([key, rows]) => {
    const first = rows[0];
    // Read the subject across the WHOLE group. A grouped ghost verdict holds a
    // company_status proposal (company, no person) beside a set_value one (the
    // person), and whichever landed first would otherwise decide which half of
    // "ClimeFi · Marc Zande" the row is allowed to say.
    const named = rows.find((r) => r.contact_first_name || r.contact_last_name) || first;
    const withCompany = rows.find((r) => r.company_name) || first;
    const poc = rows.find((r) => r.poc_first_name || r.poc_last_name);
    const contact =
      [named.contact_first_name, named.contact_last_name].filter(Boolean).join(" ").trim()
      || (poc ? [poc.poc_first_name, poc.poc_last_name].filter(Boolean).join(" ").trim() : "");
    /*
     * THE HEADLINE IS ONE CHANGE, NOT ALL OF THEM. A ghost verdict reads
     * "Rapid7 · Stuart Neilson: mark Lost (ghosted)" with the POC stamp as a
     * quieter second line, because the company's ending is the decision and the
     * contact's lead_status follows from it. Joining both into the title made a
     * two-clause sentence you had to parse to find the verb.
     */
    const lead = rows.find((r) => r.change_type === "company_status")
      || rows.find((r) => r.change_type === "company_stage")
      || first;
    const rest = rows.filter((r) => r !== lead);
    return {
      key, rows, ids: rows.map((r) => r.id),
      company: withCompany.company_name,
      companyStatus: withCompany.company_current_status,
      contact,
      headline: describeChange(lead),
      also: rest.map((r) => {
        const p = r.payload || {};
        const who = [r.contact_first_name, r.contact_last_name].filter(Boolean).join(" ")
          || r.company_name || r.contact_email;
        const to = p.lead_status || p.to;
        return to ? `${who} → ${to}` : `${who}: ${describeChange(r)}`;
      }),
      // WHOSE RECORD THIS ROW IS ABOUT. The proposal's own contact when it has
      // one, otherwise the company's POC — a company_status proposal carries no
      // contact_id and still has a person whose story explains it.
      contactId:
        rows.find((r) => r.contact_id)?.contact_id ||
        rows.find((r) => r.company_primary_contact_id)?.company_primary_contact_id ||
        null,
      evidence: first.evidence,
      source: first.evidence_source,
      rule: first.rule,
      ruleLabel: first.rule_label,
      ref: first.evidence_ref,
      resolved_at: first.resolved_at,
      resolution: first.status,
    };
  });
}

/* ---------------------------------------------------------------- the menu */

function ApproveMenu({ g, onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [onClose]);
  // The top layer while it is open: Esc closes the menu and nothing behind it.
  useEscapeLayer(true, onClose);

  const who = g.company || g.contact || "this company";
  return (
    <div className="crm-apmenu" ref={ref} role="menu" aria-label="Approve as">
      <div className="crm-apmenu__head">Approve as…</div>
      <button type="button" className="crm-apmenu__item" role="menuitem" onClick={() => onPick(null)}>
        <span>As proposed</span>
        <em>{g.headline}</em>
      </button>
      {Object.entries(ENDINGS).map(([key, end]) => (
        <button type="button" key={key} className="crm-apmenu__item" role="menuitem" onClick={() => onPick(key)}>
          <span>{end.label}</span>
          <em>{end.hint}</em>
        </button>
      ))}
      {canNotALead(g) && (
        <button type="button" className="crm-apmenu__item" role="menuitem" onClick={() => onPick("not_a_lead")}>
          <span>Not a lead</span>
          <em>delete + ignore</em>
        </button>
      )}
      {/* The question the menu would otherwise leave you hunting for. "Nothing
          happens" is a dismissal — the API refuses an empty correction — and
          saying so here is cheaper than letting it be discovered. */}
      <div className="crm-apmenu__foot">
        Want to leave {who} in the pipeline as-is? That’s not an ending — use <b>Dismiss</b>, the white button.
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- the row */

function Row({ g, muted, onApprove, onDismiss, onCorrect, busy, blockedReason = null, onOpen = null, selected = false, menuOpen = false, onMenu }) {
  const link = g.ref && /^https?:\/\//.test(g.ref) ? g.ref : null;
  const badge = BADGE[g.rule] || SOURCE_BADGE[g.source] || "PROP";
  const clickable = !!onOpen;
  const stop = (fn) => (e) => { e.stopPropagation(); if (fn) fn(); };

  return (
    <div
      className={`crm-row2 crm-aprow${muted ? " crm-row2--muted" : ""}${clickable ? " crm-row2--click" : ""}${selected ? " crm-row2--sel" : ""}`}
      onClick={clickable ? () => onOpen(g) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-expanded={clickable ? selected : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(g); }
      } : undefined}
    >
      <div className="crm-row2__icon crm-apbadge">{badge}</div>
      <div className="crm-aprow__body">
        <div className="crm-row2__line1">
          <span className="crm-aptitle">
            {g.company && <b>{g.company}</b>}
            {`${g.company && g.contact ? " · " : ""}${g.contact}${g.company || g.contact ? ": " : ""}${g.headline}`}
          </span>
          {g.rows.length > 1 && <span className="crm-apcount">{g.rows.length} changes</span>}
        </div>
        {/* The other half of the verdict, stated rather than folded into the
            headline. You are answering both; you should be able to see both. */}
        {g.also.length > 0 && (
          <div className="crm-apalso">also: {g.also.map((l) => <code key={l}>{l}</code>)}</div>
        )}
        <div className="crm-row2__line2">
          <span>{g.evidence}</span>
          {/* Only when the ref is a URL: sweep-derived refs have no page to open,
              and link-styled text that goes nowhere reads as broken. */}
          {link && (
            <a className="crm-row2__link" href={link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
              {LINK_LABEL[g.source] || "Source"} →
            </a>
          )}
        </div>
        {blockedReason && (
          <div className="crm-row2__blocked">
            Blocked: {blockedReason}
            {onDismiss && (
              <button type="button" className="crm-row2__link" onClick={stop(() => onDismiss(g))}>
                Dismiss instead?
              </button>
            )}
          </div>
        )}
      </div>
      {muted ? (
        // Three receipts, deliberately distinct: auto-applied means it HAPPENED
        // without review, superseded means it never will because the company
        // moved past it, corrected means it happened differently. Collapsing any
        // two would make different decisions look alike.
        <span className={`crm-chip-check crm-chip-check--${g.resolution}`}>
          {g.resolution === "superseded" ? "superseded" : g.resolution === "corrected" ? "corrected" : "auto-applied"}
        </span>
      ) : (
        <div className="crm-row2__actions" onClick={(e) => e.stopPropagation()}>
          <div className="crm-apsplit">
            <button className="crm-btn crm-btn--primary crm-apsplit__main" disabled={busy} onClick={() => onApprove(g)}>
              Approve
            </button>
            <button
              className="crm-btn crm-btn--primary crm-apsplit__caret"
              disabled={busy}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Approve as something else"
              onClick={() => onMenu(menuOpen ? null : g.key)}
            >
              ▾
            </button>
            {menuOpen && (
              <ApproveMenu
                g={g}
                onClose={() => onMenu(null)}
                onPick={(ending) => { onMenu(null); if (ending) onCorrect(g, ending); else onApprove(g); }}
              />
            )}
          </div>
          <button className="crm-btn crm-btn--secondary" disabled={busy} onClick={() => onDismiss(g)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- the panel */

function CorrectionForm({ ending, onCancel, onConfirm, busy, error }) {
  const spec = ENDINGS[ending];
  const [reason, setReason] = useState(spec.reasons[0][0]);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");

  return (
    <div className="crm-appanel__card crm-apcorrect">
      <div className="crm-appanel__label">Correct to · {spec.label}</div>
      <div className="crm-apcorrect__reasons">
        {spec.reasons.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`crm-apchip${reason === value ? " sel" : ""}`}
            onClick={() => setReason(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {spec.date && (
        <div className="crm-apfield">
          <span>
            {spec.date.label}
            {/* Said before you submit rather than after it is refused. The API
                enforces it either way; only one of the two is a good experience. */}
            {spec.date.required ? " (required)" : " (optional)"}
          </span>
          <DateField value={date} onChange={setDate} aria-label={spec.date.label} clearable={!spec.date.required} />
        </div>
      )}
      <label className="crm-apfield">
        <span>Note (optional)</span>
        <input
          type="text"
          value={note}
          placeholder="Mid-contract with an incumbent"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {/* The refusal lands here, on the thing you were filling in, rather than
          as a toast that floats away with the row. */}
      {error && <div className="crm-apcorrect__err">{error}</div>}
      <div className="crm-apcorrect__actions">
        <button
          className="crm-btn crm-btn--primary"
          disabled={busy || (spec.date?.required && !date)}
          onClick={() => onConfirm({ status: spec.status, reason, date, note })}
        >
          {busy ? "Saving…" : `Confirm ${spec.label.toLowerCase()}`}
        </button>
        <button className="crm-btn crm-btn--secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * The confirm for "not a lead", in the panel where the correction form would
 * sit. It deletes, so it says what goes before the button does it — the same
 * promises the contact page's modal makes, in fewer words.
 */
function NotALeadForm({ g, onCancel, onConfirm, busy, error }) {
  const [note, setNote] = useState("");
  const name = g.contact || "This person";
  return (
    <div className="crm-appanel__card crm-apcorrect">
      <div className="crm-appanel__label">Correct to · Not a lead</div>
      <p className="crm-appanel__muted">
        Deletes <b>{name}</b> and their whole timeline, and ignores their address from then on: no
        contact, no email, no proposal. {g.company ? <><b>{g.company}</b> goes with them if it holds nothing else. </> : null}
        Kept as a receipt under <b>Ignored</b> on the Leads page.
      </p>
      <label className="crm-apfield">
        <span>Why (optional)</span>
        <input
          type="text"
          value={note}
          placeholder="Friend, lunch, not business"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {error && <div className="crm-apcorrect__err">{error}</div>}
      <div className="crm-apcorrect__actions">
        <button className="crm-btn crm-btn--dark" disabled={busy} onClick={() => onConfirm({ note })}>
          {busy ? "Removing…" : "Not a lead"}
        </button>
        <button className="crm-btn crm-btn--secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/*
 * WHY THIS ROW IS HERE, AND THEN THE WHOLE RECORD.
 *
 * The first version of this panel was the mockup's card and nothing else, and it
 * cost the thing the panel had always been for: Gabriel clicks a row to dig into
 * the story and the status before ruling on it, and a four-field summary is not
 * that. Approving is a judgement made WITH the record in view
 * (feedback-crm-row-interaction), and the record is ContactDetailPage.
 *
 * So the panel is two parts, in the order you need them: what this row proposes
 * and why (the rule, their own words, the moves, and the correction form), then
 * the contact page itself — timeline, story, firmographics, quick actions, and
 * its own "Open full page" link for when the panel is not enough.
 *
 * The close button lives in the record's bar rather than here, because rendering
 * one in each half would put two ×s twenty pixels apart. A row with no contact
 * behind it gets its own, since then there is no record and no bar.
 */
function ProposalPanel({ g, context, loading, onClose, onChange, correcting, onCancelCorrection, onConfirmCorrection, onConfirmNotALead, busy, error }) {
  const first = g.rows[0];
  const quote = context?.quote;
  const lastInbound = context?.last_inbound;

  return (
    <div className="crm-appanel">
      <div className="crm-appanel__top">
        <span className="crm-appanel__label">This proposal</span>
        {!g.contactId && (
          <button className="crm-peek__close" onClick={onClose} aria-label="Close">×</button>
        )}
      </div>

      <div className="crm-appanel__card crm-approposal">
        {/* What NOTICED, and when the relationship last moved. Everything else
            about the company is on the record below; these two are facts about
            the PROPOSAL, and they are what decides how much weight it carries. */}
        <div className="crm-appanel__grid">
          <div><span>Source</span><b>{g.ruleLabel || "—"}</b></div>
          <div><span>Last touch</span><b>{shortDate(first.company_last_activity_at) || "—"}</b></div>
        </div>

        <div className="crm-appanel__label">Evidence</div>
        {loading && <p className="crm-appanel__muted">Reading the thread…</p>}
        {!loading && quote && (
          <>
            <blockquote className="crm-apquote">{quote.text}</blockquote>
            <div className="crm-apquote__by">
              {quote.from || "They"}, {quote.type === "email" ? "reply" : quote.type.replace("_", " ")} of {dayDate(quote.occurred_at)}
              {quote.thread_url && (
                <> · <a className="crm-row2__link" href={quote.thread_url} target="_blank" rel="noreferrer">Thread →</a></>
              )}
            </div>
          </>
        )}
        {/* Nothing to quote is a real answer and is said as one. The rule counted
            silence; showing an empty box would look like a failure to load. */}
        {!loading && !quote && (
          <p className="crm-appanel__muted">Nothing inbound on this record — the sweep is counting silence.</p>
        )}
        {/* The last sign of life, when it is NOT the quote. A meeting has no words
            in it, so it is named rather than quoted: its title is a label we
            wrote. */}
        {!loading && lastInbound && !lastInbound.same_as_quote && (
          <p className="crm-appanel__muted">
            Last sign of life: {lastInbound.type.replace("_", " ")}
            {lastInbound.title ? ` “${lastInbound.title}”` : ""}, {dayDate(lastInbound.occurred_at)}
          </p>
        )}

        <div className="crm-appanel__label">This row proposes</div>
        <div className="crm-apmono">
          {/* The company's move first, whatever order the rules wrote them in: it
              is the decision, and the contact's stamp follows from it. */}
          {[...g.rows].sort((a, b) => (a.company_id ? 0 : 1) - (b.company_id ? 0 : 1)).map((r) => {
            const line = proposalLine(r);
            return (
              <div key={r.id}>
                <span>{line.subject}:</span> {line.body}
              </div>
            );
          })}
        </div>

        {correcting === "not_a_lead" && (
          <NotALeadForm g={g} busy={busy} error={error} onCancel={onCancelCorrection} onConfirm={onConfirmNotALead} />
        )}
        {correcting && correcting !== "not_a_lead" && (
          <CorrectionForm
            key={correcting}
            ending={correcting}
            busy={busy}
            error={error}
            onCancel={onCancelCorrection}
            onConfirm={onConfirmCorrection}
          />
        )}
      </div>

      {/* The record. Keyed on the contact so switching rows remounts rather than
          showing the previous person's timeline while the next one loads. */}
      {g.contactId ? (
        <div className="crm-aprecord">
          {/* inline: the record stays under the proposal, in the panel layout, even
              on a phone. Its full-screen phone sheet covered the proposal's Evidence
              and the Approve ▾ correction form, and its × closed the proposal. */}
          <ContactDetailPage key={g.contactId} contactId={g.contactId} embedded inline onClose={onClose} onChange={onChange} openAction="pending" hostLabel="Approvals" />
        </div>
      ) : (
        <p className="crm-appanel__muted">
          No contact on this proposal, so there is no record to open.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ page */

export default function ApprovalsPage() {
  const [pending, setPending] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState({});
  const [menu, setMenu] = useState(null);
  // { key, ids } of the row being read, plus the ending being chosen for it.
  const [peek, setPeek] = useState(null);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const [correctError, setCorrectError] = useState("");
  const { refresh: refreshBadge } = usePending();

  const load = useCallback(async () => {
    try {
      const [p, a, sup, corr] = await Promise.all([
        api("/pending-changes?status=pending&limit=500"),
        api("/pending-changes?status=auto_applied&limit=25"),
        api("/pending-changes?status=superseded&limit=25"),
        api("/pending-changes?status=corrected&limit=25"),
      ]);
      setPending(p.changes);
      // One history, newest first, and only TODAY's: these are receipts for a
      // sitting, not an archive. A row settled last week is not "not waiting on
      // you" news.
      setReceipts(
        [...a.changes, ...sup.changes, ...corr.changes]
          .filter((c) => isToday(c.resolved_at))
          .sort((x, y) => new Date(y.resolved_at || 0) - new Date(x.resolved_at || 0))
      );
      refreshBadge();
    } catch (e) { setError(e.message); }
  }, [refreshBadge]);
  useEffect(() => { load(); }, [load]);

  // The quote is fetched per opened row rather than joined into the list: it
  // reads a body per company and the queue is drawn 500 rows at a time.
  useEffect(() => {
    if (!peek) { setContext(null); return undefined; }
    let live = true;
    setContextLoading(true);
    api(`/pending-changes/${peek.ids[0]}/context`)
      .then((c) => { if (live) setContext(c); })
      .catch(() => { if (live) setContext(null); })
      .finally(() => { if (live) setContextLoading(false); });
    return () => { live = false; };
  }, [peek]);

  // Reloaded on the way out, not just closed: the record panel can remove the
  // thing a row is about ("not a lead" purges its pending changes with it), and a
  // queue that still lists it offers an approval that can only fail.
  const closePeek = useCallback(() => {
    setPeek(null);
    setCorrecting(null);
    load();
  }, [load]);

  // Esc, one layer at a time (useEscape.js): the correction form closes before
  // the panel it sits in, and neither while typing in it or in the record.
  useEscapeLayer(!!peek, closePeek);
  useEscapeLayer(!!correcting, () => {
    if (busy) return;
    setCorrecting(null);
    setCorrectError("");
  });

  async function bulk(ids, action) {
    setBusy(true);
    try {
      const { results } = await api("/pending-changes/bulk", { method: "POST", body: JSON.stringify({ ids, action }) });
      const failures = {};
      for (const r of results || []) {
        if (r.status === "error") failures[r.id] = blockText(r);
        else if (r.status === "already_resolved") failures[r.id] = "already resolved elsewhere";
        else if (r.status === "not_found") failures[r.id] = "no longer exists";
      }
      setBlocked((prev) => {
        const next = { ...prev };
        for (const id of ids) delete next[id];
        return { ...next, ...failures };
      });
      // A row that has been ruled on leaves the list, so its panel goes with it
      // — unless the apply was refused, in which case the row is still there and
      // the reason is exactly what the panel is open for.
      setPeek((p) => (p && p.ids.some((id) => ids.includes(id) && !failures[id]) ? null : p));
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  /*
   * A correction is one request for the whole group: the server writes the
   * ending and settles every row in the same transaction, so the queue cannot
   * end up disagreeing with the record.
   */
  async function correct(ids, { status, reason, date, note }) {
    setBusy(true);
    setCorrectError("");
    try {
      const company = { status, status_reason: reason };
      const spec = Object.values(ENDINGS).find((e) => e.status === status);
      if (spec?.date && date) company[spec.date.field] = date;
      await api("/pending-changes/correct", {
        method: "POST",
        body: JSON.stringify({ ids, company, note: note || null }),
      });
      setCorrecting(null);
      setPeek(null);
      await load();
    } catch (e) {
      // Stays on the form with the fields as typed: the fix is usually one
      // field, and clearing them to say so would be a punishment.
      setCorrectError(e.message);
    } finally { setBusy(false); }
  }

  /*
   * The purge takes the contact's own proposals with it, and the company's when
   * the company goes too. A company that stays (it has other people, or a
   * history of its own) keeps its row, which the verdict has now answered —
   * so what is left of the group is dismissed, and a not_found there is the
   * purge having got to it first.
   */
  async function notALead(g, { note }) {
    setBusy(true);
    setCorrectError("");
    try {
      await api(`/contacts/${g.contactId}/not-a-lead`, {
        method: "POST",
        body: JSON.stringify({ note: note.trim() || null }),
      });
      await api("/pending-changes/bulk", { method: "POST", body: JSON.stringify({ ids: g.ids, action: "dismiss" }) })
        .catch(() => null);
      setCorrecting(null);
      setPeek(null);
      await load();
    } catch (e) {
      // The one refusal is a ruling ("that's a client — make them associated"),
      // and it is read on the form it answers.
      setCorrectError(e.message);
    } finally { setBusy(false); }
  }

  const pendingGroups = groupRows(pending);
  const receiptGroups = groupRows(receipts);

  const open = (g) => {
    setCorrecting(null);
    setCorrectError("");
    setPeek((p) => (p && p.key === g.key ? null : { key: g.key, ids: g.ids }));
  };
  const startCorrection = (g, ending) => {
    setCorrectError("");
    setPeek({ key: g.key, ids: g.ids });
    setCorrecting(ending);
  };

  const openGroup = peek && [...pendingGroups, ...receiptGroups].find((g) => g.key === peek.key);
  const settledCount = receiptGroups.length;
  const sub = `${pendingGroups.length} to answer${settledCount ? ` · ${settledCount} settled today` : ""}`;

  return (
    <>
      <Topbar title="Approvals" sub={sub} subMobile={`${pendingGroups.length} to answer`} />

      <div className={`crm-apwrap${peek ? " crm-apwrap--split" : ""}`}>
        <div className="crm-approvals">
          {error && <p className="crm-login__err">{error}</p>}
          {pendingGroups.length === 0 && (
            <div className="crm-placeholder">
              <h2>Nothing to answer</h2>
              <p>Proposed changes from calls, emails, documents and the weekly sweep land here.</p>
            </div>
          )}
          {pendingGroups.map((g) => (
            <Row
              key={g.key}
              g={g}
              busy={busy}
              blockedReason={g.ids.map((id) => blocked[id]).find(Boolean) || null}
              onApprove={(x) => bulk(x.ids, "approve")}
              onDismiss={(x) => bulk(x.ids, "dismiss")}
              onCorrect={startCorrection}
              onOpen={open}
              selected={!!peek && peek.key === g.key}
              menuOpen={menu === g.key}
              onMenu={setMenu}
            />
          ))}
          {receiptGroups.length > 0 && (
            <div className="crm-apsection">Settled today · not waiting on you</div>
          )}
          {receiptGroups.map((g) => (
            <Row key={g.key} g={g} muted onOpen={open} selected={!!peek && peek.key === g.key} />
          ))}
        </div>

        {peek && openGroup && (
          <aside className="crm-peek">
            <ProposalPanel
              key={peek.key}
              g={openGroup}
              context={context}
              loading={contextLoading}
              busy={busy}
              error={correctError}
              correcting={correcting}
              onCancelCorrection={() => { setCorrecting(null); setCorrectError(""); }}
              onConfirmCorrection={(values) => correct(openGroup.ids, values)}
              onConfirmNotALead={(values) => notALead(openGroup, values)}
              onClose={closePeek}
              onChange={load}
            />
          </aside>
        )}
      </div>
    </>
  );
}
